import { pick, prop } from 'ramda';
import {
    usePortletConfig,
    usePortletDispatch,
    usePortletSelector,
    usePortletTranslations,
    withPortlet
} from 'js/components/system/Portlet';
import React, { useMemo, useState } from 'react';
import { usePushSubscription } from 'server';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';

import Empty from 'js/components/main/Games/Empty';
import Filters from './Filters';
import Skeleton from 'js/components/main/Games/Skeleton';
import { Search } from './Search';
import { Category } from './Category';
import { useTracking } from 'js/components/main/Games/trackingApi';
import { gamesPushAction } from 'js/components/main/Games/actions';
import reducer, { initialState } from './reducer';
import { useRecentlyPlayed } from 'js/components/main/Games/useRecentlyPlayed';
import { useFiltersEnabled } from './useFiltersEnabled';
import { useCategories, useFeed } from 'js/components/main/Games/getCategories';
import { useFetchCategoriesFromBI } from './BICategories';
import useFilteredCategories from './useFilteredCategories';

import style from './index.scss';

export const GamesBare = () => {
    useFeed();
    useCategories();
    const { feedName } = usePortletSelector(pick(['feedName']));
    const t = usePortletTranslations();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const {
        isDynamicCategoriesFetched,
        isLoadedCategories
    } = usePortletSelector(pick([
        'isDynamicCategoriesFetched',
        'isLoadedCategories'
    ]));
    const { isLoggedIn } = useUserSelector(pick(['isLoggedIn']));
    const { showSearch: searchEnabled } = usePortletConfig();
    const filtersEnabled = useFiltersEnabled();
    const showHeader = searchEnabled || filtersEnabled;
    const dispatch = usePortletDispatch();
    const { categories } = usePortletSelector(pick(['categories']));
    const [isGamesRendered, setIsGamesRendered] = useState(false);

    // eslint-disable-next-line no-unused-vars
    const checkRenderedGame = (gameRef) => {
        if (gameRef) {
            setIsGamesRendered(true);
        }
    };

    const renderHeader = useMemo(() => {
        const elements = [];

        if (searchEnabled) {
            elements.push(<Search key={1} className={style.search} />);
        }

        if (filtersEnabled) {
            elements.push(<Filters key={2} className={style.filters} />);
        }

        if (isDesktop) {
            elements.reverse();
        }

        return (
            <div className={style.header}>
                {elements.map(item => item)}
            </div>
        );
    }, [filtersEnabled, searchEnabled, isDesktop]);

    useFetchCategoriesFromBI();

    const isRecentlyPlayedFetching = useRecentlyPlayed();

    const { isFavoritesFetching, dynamicTags } = useAppSelector(pick(['isFavoritesFetching', 'dynamicTags']));
    const shouldRenderSkeleton =
        isRecentlyPlayedFetching || isFavoritesFetching || !isDynamicCategoriesFetched || !isGamesRendered;

    usePushSubscription(31133, () => pushMessage => dispatch(gamesPushAction(pushMessage)));

    useFilteredCategories(filtersEnabled);

    useTracking();

    const { tabs, sliders, length: categoriesLength } = usePortletSelector(prop('groupedFilteredCategories'));
    const orderCategory = dynamicTags && dynamicTags.DTAG_CATEGORIESORDER;
    const arrayOfOrder = orderCategory?.split(',');
    const updatedArray = arrayOfOrder && arrayOfOrder.reduce((acc, curr) => {
        const r = curr.split('=');
        const res = r[1].split('|');
        // eslint-disable-next-line no-param-reassign
        curr = r[0];
        acc[curr] = {
            favorites: res[0],
            recentlyplayed: res[1]
        };
        return acc;
    }, {});

    const isSortingApply = updatedArray && updatedArray[feedName] && sliders.length && isLoggedIn;
    let sortedArray = [];
    const getOrderSettings = () => {
        if ((sliders.length + tabs.length) === categoriesLength && categories.length) {
            const favorites = 'favorites';
            const recentlyPlayed = 'recentlyplayed';
            const favoritesIndex = Number(updatedArray[feedName][favorites]) - 1;
            const recentlyPlayedIndex = Number(updatedArray[feedName][recentlyPlayed]) - 1;
            sortedArray = [...sliders, ...tabs];
            const recentlyPlayedValue = sortedArray.find(el => el.type === recentlyPlayed);
            const favouriteValue = sortedArray.find(el => el.type === favorites);
            const getUpdatedCategories = (firstCheckingIndex, firstCheckingValue, secondCheckingIndex, secondCheckingValue) => {
                const clearArray = sortedArray.filter(el => el.type !== recentlyPlayed && el.type !== favorites);
                if (!clearArray.length) {
                    return;
                }

                firstCheckingValue && clearArray.splice(firstCheckingIndex, 0, firstCheckingValue);
                secondCheckingValue && clearArray.splice(secondCheckingIndex, 0, secondCheckingValue);

                return clearArray;
            };

            return favoritesIndex > recentlyPlayedIndex ?
                getUpdatedCategories(recentlyPlayedIndex, recentlyPlayedValue, favoritesIndex, favouriteValue) :
                getUpdatedCategories(favoritesIndex, favouriteValue, recentlyPlayedIndex, recentlyPlayedValue);
        }
    };
    const showCategories = isSortingApply ? getOrderSettings() : [...sliders, ...tabs];

    if (shouldRenderSkeleton && isDesktop && !categories.length) {
        return <Skeleton />;
    }

    return (
        <div className={style.container}>
            {showHeader && renderHeader}
            {showCategories?.length > 0 && showCategories.map((category, index) => (
                <Category
                    key={category.cid}
                    isGamesRendered
                    isFirstCategory={index === 0}
                    {...category}
                />
            ))}
            {categoriesLength === 0 && isLoadedCategories && <Empty text={t('empty')} />}
        </div>
    );
};

export const Games = withPortlet({
    reducer,
    initialState,
    name: 'Games'
})(GamesBare);

export default Games;
